<template>
  <div class="gallery">
    <category-bar></category-bar>

    <div class="d-flex display-1 text-center mb-0" style="height: 96px">
      <input
        type="text"
        placeholder="Search by"
        class="text-center"
        :value="$route.params.search"
        @input="search = $event.target.value"
        @keyup.enter="enter"
        ref="search"
      />
    </div>

    <div class="filter">
      <hr />
      <b-container>
        <b-row>
          <b-col md="6" class="d-flex align-items-center">
            <b-col cols="4">
              <multi-select
                title="타입"
                v-model="type"
                source="name"
                :options="types"
                :max="1"
              >
              </multi-select>
            </b-col>
            <b-col cols="4" v-if="type.name === 'Projects'">
              <ordering :options="orderings" v-model="ordering" />
            </b-col>
            <b-col cols="4">
              <multi-select
                title="장르"
                v-model="category"
                source="category"
                :options="categories"
                :max="1"
                align="right"
              >
              </multi-select>
            </b-col>
          </b-col>

          <b-col
            md="6"
            class="d-flex align-items-center"
            v-if="type.name === 'Projects'"
          >
            <b-col cols="4">
              <multi-select
                title="분야"
                v-model="field"
                source="field"
                :options="fields"
                :max="1"
              >
              </multi-select>
            </b-col>
            <b-col cols="4">
              <multi-select
                title="컨셉"
                v-model="concept"
                source="concept"
                :options="concepts"
                :max="1"
              >
              </multi-select>
            </b-col>
            <b-col cols="4">
              <color-picker :colors="colors" v-model="color" />
            </b-col>
          </b-col>
        </b-row>
      </b-container>
      <hr />
    </div>

    <div class="splash d-flex display-1 justify-content-center" v-if="!loaded">
      <span class="text-center">
        <font-awesome-icon slot="spinner" icon="spinner" size="2x" pulse />
      </span>
    </div>
    <template v-else-if="type.name === 'Projects'">
      <artworks v-model="artworks" :fixed-layout="fixedLayout" />
    </template>
    <template v-else>
      <artists v-model="artists" />
    </template>
    <slot />
  </div>
</template>

<script>
import Vue from "vue";
import * as moment from "moment";
import { Artwork, User } from "../models";
import Artists from "../components/Artists";
import CategoryBar from "../components/CategoryBar";
import MultiSelect from "../components/MultiSelect";
import ColorPicker from "../components/ColorPicker";
import NameFilter from "../components/NameFilter";
import Ordering from "../components/Ordering";

export default Vue.component("gallery", {
  components: {
    Artists,
    CategoryBar,
    MultiSelect,
    ColorPicker,
    NameFilter,
    Ordering
  },
  data() {
    return {
      search: "",
      fixedLayout: true,
      ordering: {
        ordering: {
          name: "Most Recent",
          value: null
        },
        created__gte: {
          name: "All time",
          value: null
        }
      },
      orderings: {
        ordering: [
          {
            name: "Most Recent",
            value: null
          },
          {
            name: "Most Viewed",
            value: "-hit"
          }
        ],
        created__gte: [
          {
            name: "All time",
            value: null
          },
          {
            name: "1 month",
            value: moment()
              .subtract(1, "months")
              .format("YYYY-MM-DD 00:00:00")
          },
          {
            name: "3 months",
            value: moment()
              .subtract(3, "months")
              .format("YYYY-MM-DD 00:00:00")
          }
        ]
      },
      types: [
        {
          name: "Projects"
        },
        {
          name: "Artists"
        }
      ],
      categories: window.categories,
      fields: window.fields,
      concepts: window.concepts,
      colors: window.colors,
      type: { name: "Projects" },
      category: null,
      field: null,
      concept: null,
      color: null,
      artworks: null,
      artists: null,
      loaded: false
    };
  },
  watch: {
    "$route.params.category": {
      handler(category) {
        if (!category) {
          return;
        }

        if (category === "all") {
          category = null;
        }

        if (category) {
          this.category = this.categories.find(c => c.category == category);
        } else {
          this.category = null;
        }
      },
      immediate: true
    },
    "$route.params.search"(search) {
      this.search = search;
      this.$refs.search.focus();

      if (this.type.name !== "Projects") {
        this.type = {
          name: "Projects"
        };
      } else if (this.search) {
        this.load();
      }
    },
    async "ordering.ordering.value"() {
      await this.load();
    },
    async "ordering.created__gte.value"() {
      await this.load();
    },
    async type() {
      this.$refs.search.value = this.search = "";
      this.artworks = this.artists = {
        results: []
      };

      await this.load();
    },
    async category(category) {
      this.$router.replace({
        name: "gallery",
        params: {
          category: category ? category.category : "all"
        }
      });

      await this.load();
    },
    async field() {
      await this.load();
    },
    async concept() {
      await this.load();
    },
    async color() {
      await this.load();
    }
  },
  beforeMount() {
    if (this.$route.params.tag) {
      this.search = this.$route.params.tag;
    }
  },
  async mounted() {
    await this.load();
    window.moment = moment;
  },
  methods: {
    async enter() {
      this.load();
    },
    async load() {
      this.loaded = false;

      const { search, ordering, category, field, concept, color } = this;

      if (this.type.name === "Projects") {
        this.artworks = await new Artwork().get({
          search,
          ordering: ordering.ordering.value,
          created__gte: ordering.created__gte.value,
          categories__category: category ? category.category : null,
          fields__field: field ? field.field : null,
          concepts__concept: concept ? concept.concept : null,
          color: color ? color.id : null
        });
      } else {
        this.artists = await new User().get({
          search,
          categories__category: category ? category.category : null,
          is_premium: true
        });
      }

      this.loaded = true;
    }
  }
});
</script>

<style lang="scss" scoped>
input {
  margin: auto;
  width: 180px;
  height: 32px;
  background-color: #f7f7f7;
  border: none;
  border-radius: 15px;
  outline: none;
  padding-left: 10px;
  padding-right: 10px;
}

select {
  font-size: 13px;
  border: 0;

  &:focus {
    box-shadow: none;
  }
}

hr {
  margin: 0;
}

.filter {
  margin-bottom: 32px;

  /deep/ .dropdown-toggle {
    min-height: 40px;
  }
}

.splash {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: rgba(0, 0, 0, 0.5);
}
</style>
